import { Form } from 'antd';
import * as Components from 'components';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RevenueBarChart } from '../components/RevenueBarChart';
import { RevenueFilter } from '../components/RevenueFilter';
import { RevenueTable } from '../components/RevenueTable';
import { getRevenueBarChartHandler } from '../handlers/revenueBarChartHandler';
import { getRevenueFilterHandler } from '../handlers/revenueFilterHandler';
import { revenueDashboardService } from '../services/revenueDashboardService';
import { RevenueInterface } from '../types/revenueDashboardType';

export const RevenueDashboard: React.FC = () => {
  const [form] = Form.useForm();
  const [lodingState, setLodingState] = useState<{
    table: boolean;
    chart: boolean;
  }>({
    table: true,
    chart: true,
  });
  const [searchParams, setSearchParams] = useSearchParams({
    start_yearmon: '2022-01',
    end_yearmon: '2025-01',
    group_by: 'yearmon',
  });
  const [filterParams, setFilterParams] = useState<Record<string, string>>({
    start_yearmon: '2022-01',
    end_yearmon: '2025-01',
    group_by: 'yearmon',
  });
  const [, setSelectedList] = useState<number[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedLegend, setSelectedLegend] = useState<string | null>(null);
  const [hoverLegend, setHoverLegend] = useState<string | null>(null);
  const [filteredBarChartData, setFilteredBarChartData] = useState<RevenueInterface[]>([]);
  const { handleLegendClick, handleBarClick } = getRevenueBarChartHandler({
    selectedMonth,
    selectedLegend,
    setSelectedMonth,
    setSelectedLegend,
  });

  const { handleFilterSubmit, handleFilterReset } = getRevenueFilterHandler({
    searchParams,
    filterParams,
    setSearchParams,
  });

  const fetchData = async (
    params: URLSearchParams
  ): Promise<{ data: RevenueInterface[]; count: number }> => {
    const response = await revenueDashboardService.fetch(params);
    setLodingState({ ...lodingState, table: false });
    return { data: response.results, count: response.count };
  };

  const fetchFilteredBarChartData = async (): Promise<void> => {
    const response = await revenueDashboardService.fetchAll(
      new URLSearchParams({
        ...Object.fromEntries(searchParams.entries()),
        order: 'asc',
      })
    );
    setFilteredBarChartData(response);
    setLodingState({ ...lodingState, chart: false });
  };

  useEffect(() => {
    setLodingState({ ...lodingState, table: true, chart: true });
    form.setFieldsValue({
      start_yearmon: dayjs(searchParams.get('start_yearmon')),
      end_yearmon: dayjs(searchParams.get('end_yearmon')),
      content_provider_ids: searchParams.get('content_provider_ids')?.split(',').filter(Boolean),
      season_ids: searchParams.get('season_ids')?.split(',').filter(Boolean),
      group_by: searchParams.get('group_by'),
    });

    fetchFilteredBarChartData().then();
  }, [searchParams]);

  useEffect(() => {
    setLodingState({ ...lodingState, table: true, chart: true });
    fetchFilteredBarChartData();
  }, []);

  return (
    <Components.BasePageComponent>
      <div style={{ padding: 20 }}>
        <RevenueFilter
          form={form}
          searchParams={searchParams}
          handleFilterValueChange={setFilterParams}
          handleFilterSubmit={handleFilterSubmit}
          handleFilterReset={handleFilterReset}
        />
        {lodingState.table && lodingState.chart ? (
          <Components.LoadingSpinnerComponent />
        ) : (
          <>
            <RevenueBarChart
              searchParams={searchParams}
              filteredBarChartData={filteredBarChartData}
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
              hoverLegend={hoverLegend}
              setHoverLegend={setHoverLegend}
              selectedLegend={selectedLegend}
              handleLegendClick={handleLegendClick}
              handleBarClick={handleBarClick}
            />
            <RevenueTable
              dataFetch={fetchData}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              setSelectedList={setSelectedList}
            />
          </>
        )}
      </div>
    </Components.BasePageComponent>
  );
};

export default RevenueDashboard;
